import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import type { LabourEntry, LabourScope } from '../../api/execution'

/**
 * Correcting a headcount already recorded.
 *
 * <p>The split by skill has to keep adding up to the split by gender - the database
 * enforces it - so the form shows the running totals and refuses to save while they
 * disagree, rather than letting the server reject the entry after the fact.</p>
 */
export function LabourEditForm({
  entry,
  contractors,
  pending,
  onCancel,
  onSave,
}: {
  entry: LabourEntry
  contractors: { id: string; label: string }[]
  pending: boolean
  onCancel: () => void
  onSave: (body: Record<string, unknown>) => Promise<void>
}) {
  const [form, setForm] = useState({
    scope: (entry.labourScope ?? 'OWN') as LabourScope,
    contractorId: entry.contractorId ?? '',
    plannedWorkers: entry.plannedWorkers != null ? String(entry.plannedWorkers) : '',
    maleCount: String(entry.maleCount ?? 0),
    femaleCount: String(entry.femaleCount ?? 0),
    skilledCount: String(entry.skilledCount ?? 0),
    semiSkilledCount: String(entry.semiSkilledCount ?? 0),
    unskilledCount: String(entry.unskilledCount ?? 0),
    workingHours: String(entry.workingHours ?? 8),
    overtimeHours: String(entry.overtimeHours ?? 0),
    workDescription: entry.workDescription ?? '',
    remarks: entry.remarks ?? '',
  })

  const set = (field: keyof typeof form) => (value: string) => setForm({ ...form, [field]: value })
  const byGender = Number(form.maleCount || 0) + Number(form.femaleCount || 0)
  const bySkill =
    Number(form.skilledCount || 0) + Number(form.semiSkilledCount || 0) + Number(form.unskilledCount || 0)
  const mismatch = byGender !== bySkill

  return (
    <>
      <DialogTitle>
        Edit {entry.labourCategoryName}
        <Typography variant="body2" color="text.secondary">
          The two splits must describe the same people.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            mb: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: form.scope === 'CONTRACTOR' ? '1fr 2fr' : '1fr',
            },
          }}
        >
          <TextField
            select
            label="Whose labour"
            value={form.scope}
            onChange={(e) => {
              const scope = e.target.value as LabourScope
              setForm({ ...form, scope, contractorId: scope === 'OWN' ? '' : form.contractorId })
            }}
          >
            <MenuItem value="OWN">Own workforce</MenuItem>
            <MenuItem value="CONTRACTOR">Contractor supplied</MenuItem>
          </TextField>
          {form.scope === 'CONTRACTOR' && (
            <TextField
              select
              label="Contractor"
              value={form.contractorId}
              error={!form.contractorId}
              onChange={(e) => set('contractorId')(e.target.value)}
            >
              <MenuItem value="">Pick one</MenuItem>
              {contractors.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
        <TextField
          label="Planned workers"
          type="number"
          value={form.plannedWorkers}
          onChange={(e) => set('plannedWorkers')(e.target.value)}
          helperText="What was expected. The counts below are who actually turned up."
          sx={{ mb: 2, width: { xs: '100%', sm: 260 } }}
        />
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' } }}>
          <TextField label="Male" type="number" value={form.maleCount} onChange={(e) => set('maleCount')(e.target.value)} />
          <TextField label="Female" type="number" value={form.femaleCount} onChange={(e) => set('femaleCount')(e.target.value)} />
          <TextField label="Total by gender" value={byGender} disabled />
          <TextField label="Skilled" type="number" value={form.skilledCount} onChange={(e) => set('skilledCount')(e.target.value)} />
          <TextField label="Semi-skilled" type="number" value={form.semiSkilledCount} onChange={(e) => set('semiSkilledCount')(e.target.value)} />
          <TextField label="Unskilled" type="number" value={form.unskilledCount} onChange={(e) => set('unskilledCount')(e.target.value)} />
        </Box>
        {mismatch && (
          <Alert severity="error" sx={{ mt: 2 }}>
            The skill split adds to {bySkill}, the gender split to {byGender}. They have to
            match before this can be saved.
          </Alert>
        )}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mt: 2 }}>
          <TextField label="Working hours" type="number" value={form.workingHours} onChange={(e) => set('workingHours')(e.target.value)} />
          <TextField label="Overtime hours" type="number" value={form.overtimeHours} onChange={(e) => set('overtimeHours')(e.target.value)} />
        </Box>
        <TextField
          label="Work done"
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
          value={form.workDescription}
          onChange={(e) => set('workDescription')(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          disabled={
            pending ||
            mismatch ||
            // Zero is allowed against a plan — "ten expected, none came" is a real day.
            (bySkill === 0 && Number(form.plannedWorkers || 0) === 0) ||
            (form.scope === 'CONTRACTOR' && !form.contractorId)
          }
          onClick={() =>
            onSave({
              labourCategoryId: entry.labourCategoryId,
              contractorId: form.scope === 'CONTRACTOR' ? form.contractorId : undefined,
              plannedWorkers: form.plannedWorkers === '' ? undefined : Number(form.plannedWorkers),
              maleCount: Number(form.maleCount || 0),
              femaleCount: Number(form.femaleCount || 0),
              skilledCount: Number(form.skilledCount || 0),
              semiSkilledCount: Number(form.semiSkilledCount || 0),
              unskilledCount: Number(form.unskilledCount || 0),
              workingHours: Number(form.workingHours || 0),
              overtimeHours: Number(form.overtimeHours || 0),
              workDescription: form.workDescription || undefined,
              remarks: form.remarks || undefined,
            })
          }
        >
          {pending ? 'Saving...' : 'Save changes'}
        </Button>
      </DialogActions>
    </>
  )
}
